'use client';

import { fetchCareers } from '@eliadmin/core/src/api/general/general-api';
import { JobOfferCard } from '@eliadmin/core/src/components/shared/job-offer-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Tabs, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';

type CareerListOffer = {
  name: string;
  slug: string;
  countries: {
    id: string;
    name: string;
    is_default?: boolean;
  }[];
  salary: string;
  employment_type: {
    id: number;
    name: string;
  };
  date_start: string;
  position_type: string;
  employment_position: string;
};

export const OffersList = () => {
  const locale = useLocaleStore(s => s.currentLocale);
  function allByLocale(locale: string): string {
    const urlMap: { [key: string]: string } = {
      sk: 'Všetky',
      en: 'All',
      de: 'Alle',
    };
    return `${urlMap[locale] || urlMap['sk']}`;
  }

  const allLocale = allByLocale(locale);

  const [activeTab, setActiveTab] = useState(allLocale);

  const resultsPerPage = 8; // Adjusted as per your requirement
  const referencesQuery = useSuspenseInfiniteQuery({
    queryKey: ['careers'],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchCareers<CareerListOffer>({
        locale,
        query: {
          page: pageParam,
          results: resultsPerPage,
        },
      });
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage?.meta?.current_page < lastPage?.meta?.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
    initialPageParam: 1,
  });

  const offers = referencesQuery.data?.pages.flatMap(page => page.data) ?? [];
  const allOffers = referencesQuery.data?.pages[0]?.meta?.total ?? 0;
  const shownOffers = Math.min(referencesQuery.data?.pages.length * resultsPerPage, allOffers);

  function createCuratedOffersList(offers) {
    const uniquePositions = new Set();
    const curatedOffers = [];
    const additionalOffers = [];

    offers.forEach(offer => {
      if (!uniquePositions.has(offer.employment_position)) {
        uniquePositions.add(offer.employment_position);
        curatedOffers.push(offer);
      } else {
        additionalOffers.push(offer);
      }
    });

    return [...curatedOffers, ...additionalOffers];
  }

  // Adjusted filteredOffers function
  function filteredOffers(offers, activeTab) {
    if (activeTab === allLocale) {
      return createCuratedOffersList(offers); // Use curated list for "Všetky"
    } else {
      return offers.filter(offer => offer.employment_position === activeTab); // Regular filtering for other tabs
    }
  }

  const visibleOffers = filteredOffers(offers, activeTab); // Adjust this line if necessary

  const { t } = useTranslations();

  return (
    <Container className={cn('', 'ea-offers-page__container')}>
      <Tabs
        defaultValue={activeTab}
        onValueChange={setActiveTab}
        className={cn('', 'ea-offers-page__tabs')}
      >
        <div className={cn('', 'ea-offers-page__tabs-header')}>
          <TabsList className={cn('', 'ea-offers__tabs-list')}>
            <TabsTrigger value={allLocale} className={cn('', 'ea-offers__tabs-trigger')}>
              {allLocale}
            </TabsTrigger>
            {[...new Set(offers.map(offer => offer.employment_position))].map(position => (
              <TabsTrigger
                key={position}
                value={position}
                className={cn('', 'ea-offers__tabs-trigger')}
              >
                {position}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className={cn('', 'ea-offers-list__shown-offers')}>
            <p>{`1 - ${shownOffers} ${t('text809')} ${allOffers} ${t('text810')}`}</p>
          </div>
        </div>
      </Tabs>
      <div className={cn('', 'ea-offers-list__wrapper')}>
        {visibleOffers.map((offer, index) => (
          <JobOfferCard key={index} offer={offer} />
        ))}
      </div>
      <Button
        onClick={() => referencesQuery.fetchNextPage()}
        isLoading={referencesQuery.isFetchingNextPage}
        variant={'secondary'}
      >
        {t('text793')}
      </Button>
    </Container>
  );
};
